import { createFileRoute } from "@tanstack/react-router";
import { CostPageLayout } from "@/components/cost/CostPageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/tile-roof-replacement-cost-in-phoenix";
const TITLE = "Tile Roof Replacement (R&R) Cost in Phoenix (2026) | Durafoam";
const DESCRIPTION =
  "What a tile roof remove-and-replace costs in Phoenix: per-square-foot pricing, what drives the number, and how to avoid replacing tiles you don't need to.";

const faqs = [
  { q: "How much does tile R&R cost in Phoenix?", a: "Most tile remove-and-replace (lift tiles, replace underlayment, reset tiles) runs $4.50–$7.50 per square foot. A typical 2,000 sq ft roof falls in the $9,000–$15,000 range." },
  { q: "Do I need new tiles?", a: "Usually not. Concrete and clay tiles routinely last 50+ years. The underlayment underneath is what fails — typically at 20–25 years in Phoenix sun. R&R reuses your tiles." },
  { q: "What if my tiles are discontinued?", a: "We hold replacements for breakage during R&R, and we source matching salvage tiles from yards across the Valley if needed. Bring photos and we'll confirm availability." },
  { q: "Can you just repair sections?", a: "Yes. If the underlayment is failing in one area only — usually north slopes or valleys — we can do partial R&R for a fraction of the full cost." },
];

export const Route = createFileRoute("/tile-roof-replacement-cost-in-phoenix")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: "Tile Roof Replacement",
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <CostPageLayout
      pageSlug="tile-roof-replacement-cost-in-phoenix"
      eyebrow="Phoenix cost guide"
      h1="Tile Roof Replacement (R&R) Cost in Phoenix"
      subhead="What a real tile remove-and-replace costs in 2026 across the Phoenix Valley — and how to avoid paying for tiles you don't need to replace."
      heroBullets={[
        "Typical R&R: $4.50–$7.50 per sq ft",
        "Most 2,000 sq ft roofs: $9,000–$15,000",
        "Tiles reused — underlayment is what fails",
        "Includes lift, new underlayment, reset",
      ]}
      intro={
        <>
          <p>
            Most "tile roof replacement" in Phoenix isn't actually replacing the tiles — it's a
            tile remove-and-replace (R&R) where we lift the existing tiles, install new
            underlayment, then reset the tiles. Typical 2026 pricing is{" "}
            <strong>$4.50–$7.50 per square foot</strong>, putting a 2,000 sq ft roof in the{" "}
            <strong>$9,000–$15,000</strong> range.
          </p>
          <p>
            Concrete and clay tiles routinely outlast 2–3 underlayment cycles. If your tiles are
            still sound, paying for new tiles is money lit on fire.
          </p>
        </>
      }
      pricingRows={[
        { label: "Partial R&R (north slope only)", range: "$2,800–$5,500", notes: "Common 20–25 year fix" },
        { label: "Typical home (2,000 sq ft)", range: "$9,000–$15,000", notes: "Full tile R&R" },
        { label: "Larger home (2,500–3,000 sq ft)", range: "$13,000–$22,000", notes: "Standard R&R" },
        { label: "Full tile replacement (new tiles)", range: "$8.50–$14.00 / sq ft", notes: "Only when tiles unusable" },
        { label: "Upgrade to high-temp underlayment", range: "+$0.50–$1.00 / sq ft", notes: "Longer service life" },
      ]}
      factors={[
        { title: "Roof pitch & access", body: "Steeper roofs need staging and more careful tile handling. Single-story walkable pitches are the most affordable." },
        { title: "Underlayment type", body: "Standard 30-lb felt is base. High-temp synthetic underlayments last longer in Phoenix sun and add modest cost." },
        { title: "Tile breakage", body: "Concrete tile breaks 5–10% during R&R. We include reasonable breakage; unusual breakage on brittle clay tile adds cost." },
        { title: "Flashings & valleys", body: "All flashings, valleys, and pipe boots are replaced at R&R. Custom metalwork adds cost vs. stock parts." },
      ]}
      faqs={faqs}
      ctaTitle="Get a real tile R&R number"
      ctaBody="Use the instant calculator, or schedule a free inspection — we'll lift a few tiles, check the underlayment, and write you a quote you can trust."
    />
  );
}
